import { motion } from "framer-motion";

// Standalone, brand-tied spinning steering-wheel "O" — the same icon that
// lives inside the MOAP wordmark, but freed from the static logo PNG so we
// can animate it independently.
//
// Use cases:
//   - mode="loading"  → continuous CW rotation, smooth + steady; drop in
//                       anywhere we need to signal "work in progress"
//                       (Suspense fallback, form-submit pending, API call
//                        in flight, lazy-loaded routes booting up).
//   - mode="wobble"   → continuous CW spin then CCW spin then repeat —
//                       like the steering wheel is being turned back and
//                       forth. Brief pause between cycles so the eye gets
//                       a moment to rest. Good as a decorative on-page
//                       animation that signals "this thing is alive".
//   - mode="static"   → no animation. Useful for decorative placement on
//                       pages where motion would compete with other animations.
//
// `size` is the rendered diameter in pixels (default 64). The underlying PNG
// has whitespace padding around the icon, so we crop visually by sizing the
// outer span to the icon's actual proportion (≈51% of the PNG width) and
// using a negative inset on the img.

const SPIN_INFINITE = {
  rotate: 360,
  transition: { duration: 1.8, ease: "linear", repeat: Infinity },
};

// Continuous wobble: 0 → +360 → 0 keyframes (full CW spin then CCW back),
// loops forever with a brief pause between cycles so the rest position is
// readable for a beat before the next turn.
const WOBBLE_LOOP = {
  rotate: [0, 360, 0],
  transition: {
    duration:    2.4,
    ease:        "easeInOut",
    repeat:      Infinity,
    repeatDelay: 0.6,
    delay:       0.15,   // wait a beat after mount before the first cycle
  },
};

// The icon occupies roughly 51% × 41% of the 1080×1350 source PNG (measured
// by canvas pixel scan). Crop visually by scaling up + clipping with a
// transformed inset on the img.
const ICON_W_PCT_OF_SOURCE = 0.51;
const ICON_H_PCT_OF_SOURCE = 0.41;
const ICON_CX_PCT_OF_SOURCE = 0.497;
const ICON_CY_PCT_OF_SOURCE = 0.492;

export const SpinningO = ({ size = 64, mode = "loading", style }) => {
  // Render the source PNG at a scale where its icon-portion matches `size`,
  // then crop the visible area to just the icon. The img is positioned so
  // its icon-center sits at the wrapper's center.
  const sourceW = size / ICON_W_PCT_OF_SOURCE;
  const sourceH = sourceW * (1350 / 1080);
  const imgLeft = size / 2 - ICON_CX_PCT_OF_SOURCE * sourceW;
  const imgTop  = size / 2 - ICON_CY_PCT_OF_SOURCE * sourceH;

  const animateProps =
    mode === "loading" ? SPIN_INFINITE :
    mode === "wobble"  ? WOBBLE_LOOP   :
    /* static */         { rotate: 0, transition: { duration: 0 } };

  return (
    <span
      aria-label={mode === "loading" ? "Loading" : undefined}
      role={mode === "loading" ? "status" : undefined}
      style={{
        display: "inline-block",
        width:   size,
        height:  size,
        position: "relative",
        overflow: "hidden",
        ...style,
      }}
    >
      <motion.img
        src="/logos/logo-o.png"
        alt=""
        aria-hidden="true"
        animate={animateProps}
        style={{
          position: "absolute",
          top:      imgTop,
          left:     imgLeft,
          width:    sourceW,
          height:   sourceH,
          transformOrigin: `${ICON_CX_PCT_OF_SOURCE * 100}% ${ICON_CY_PCT_OF_SOURCE * 100}%`,
          pointerEvents: "none",
        }}
      />
    </span>
  );
};
